/**
 * Format: Product
 * ------------------------------------------------------------------------------
 * - Format a product response from the Storefront API
 * - Outputted object should match that of the formatted Shopify API helper.
 * - https://shopify.dev/api/storefront/2023-07/objects/Product
 */
import { v4 as uuidv4 } from 'uuid'
import { formatMoney } from '@shopify/theme-currency'

import { decodeApiId, transformEdges } from '~/helpers/graphql'
import { getCloudinarySizedImageUrl, getCloudinaryBaseUrl } from '~/helpers/format'

import { getFormattedCloudinaryImages, updateProductImageCDN } from '~/format/utils'
import {
  getProductTagValueByKey,
  isProductRestricted,
  isComingSoonProduct,
} from '~/helpers/product'
import { getPackShotImage, parseJSONUntilObject } from '~/helpers/utils'
import { stringToHandle, stringToTitleCase } from '~/helpers/strings'

import {
  getFormattedBadges,
  getFormattedLabels,
  getFormattedStyles,
  getFormattedTrendingBanners,
  getFormattedTypes,
  getFormattedWishlist,
  getFormattedWebExclusive,
  getTypesFromMetafields,
  getInternationalSizing,
} from '~/format/utils'

/**
 * Formats response from Shopify storefront.
 * @param {Object} responseProduct - GraphQL Storefront response.product.
 * @param {String} currentFlexiFashionHandle - Handle of the FlexiFashion variant.
 * @returns {Object}
 */
export function formatProduct({ responseProduct, currentFlexiFashionHandle = false }) {
  const product = responseProduct

  const handle = product.handle
  const { options, usesInternationalSizing } = getFormattedOptions(product)
  const currentFlexiFashion = getFormattedFlexiFashionData(product, currentFlexiFashionHandle)
  const tags = product.tags

  const url = mint.routes.root === '/'
    ? `//${location.host}/products/${handle}`
    : `//${location.host}${mint.routes.root}/products/${handle}`

  const formattedProduct = {
    attributes: {},
    available: product.available,
    badges: getFormattedBadges(tags),
    compareAtPrice: getFormattedPrice(product.compareAtPriceRange),
    collections: getFormattedCollections(product.collections),
    comingSoon: isComingSoonProduct(tags),
    completeTheLook: [],
    colour: getFormattedColour(product),
    ...getFormattedDescriptions(product),
    handle,
    flexiFashion: getFormattedFlexiFashion(currentFlexiFashion),
    id: decodeApiId(product.id),
    images: getFormattedImages(product, currentFlexiFashion),
    inventory: Number(product.totalInventory),
    isPatchworks: Boolean(product.metaStyleId?.value),
    key: uuidv4(),
    label: getFormattedLabels(tags),
    linkedStyles: [],
    liveShopping: {
      cta: product.metaLiveShoppingCTA?.value,
      id: product.metaLiveShoppingID?.value,
      image: product.metaLiveShoppingImage?.reference?.image,
    },
    options,
    price: getFormattedPrice(product.priceRange),
    shopTheLook: getFormattedShopTheLook({ product, currentFlexiFashion }),
    source: 'shopify',
    styleId: stringToHandle(getProductTagValueByKey(tags, 'style_id')),
    styles: getFormattedStyles(tags),
    tags,
    title: product.title,
    types: {
      ...getFormattedTypes(tags),
      ...getTypesFromMetafields(product),
    },
    url,
    usesInternationalSizing,
    webExclusive: getFormattedWebExclusive(tags),
    wishlist: getFormattedWishlist(tags),
  }

  /**
   * Attributes can be considered 'Misc' and anything that could be used for
   * marketing, attribution, dataLayers etc.
   * - This object is passed into the main 'Add to Cart' function and
   *   individual bits of data can be distributed to the dataLayer, Elevar etc.
   * - Adding values here allows us to add new data without modifying additional
   *   areas of the 'Add to Cart' process.
   */
  if (currentFlexiFashion) {
    formattedProduct.attributes.flexiFashionName = currentFlexiFashion.name
  }

  /**
   * Values that require a formatted product object to set.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.giftCard = formattedProduct.types.shopify === 'Gift Cards'
  formattedProduct.variants = getFormattedVariants(product, formattedProduct.giftCard)
  formattedProduct.onlyHasDefaultVariant = Boolean(formattedProduct.variants.length === 1)

  formattedProduct.restricted = isProductRestricted(formattedProduct),
  formattedProduct.trendingBanner = getFormattedTrendingBanners(formattedProduct)

  return formattedProduct
}

/**
 * Formats `linked-style-products.gql` response.
 * - Formats `LinkedStyleReference` response to be appended to `formatProduct`
 * @param {Object} response - GraphQL Storefront response.
 * @param {String} parentHandle - Handle of the product this array is being added to.
 * @param {String} [tag] - `LinkedStyleReference` tag - for debugging.
 *
 * @returns {Object}
 */
export function formatLinkedStyles(response, parentHandle, tag = false) {
  const products = response.products

  if (!products || !Object.keys(products).length) {
    mint.Log(
      'warning',
      'Unable to fetch Linked Styles. The products are likely either unavailable or the tags are misconfigured',
      false,
      {
        parentHandle,
        tag,
      },
    )

    return false
  }

  let currentStyle = false

  const formattedStyles = transformEdges(products).map((product) => {
    const handle = product.handle
    const tags = product.tags

    const isCurrentStyle = Boolean(handle === parentHandle)

    /**
     * In April '23, Cloudinary was introduced
     * - New products will have imagery sourced from Cloudinary
     * - Old products have not been migrated, so a new swatch field was made
     * - Check for both swatches until legacy is phased out
     */
    const cloudinarySwatch = product.metaCloudinarySwatch?.value || false
    let swatch = product.metaSwatchImage?.reference?.image?.url || false

    if (cloudinarySwatch) {
      const baseURL = getCloudinaryBaseUrl()
      swatch = getCloudinarySizedImageUrl({
        baseUrl: baseURL,
        publicId: cloudinarySwatch,
        transformations: [
          'w_40',
          'h_40',
        ],
      })
    }

    const colour = {
      group: stringToTitleCase(getProductTagValueByKey(tags, 'colour_group')),
      handle: stringToHandle(getProductTagValueByKey(tags, 'colour')),
      name: stringToTitleCase(getProductTagValueByKey(tags, 'colour')),
      swatch: swatch,
    }

    colour.swatchAlt = `${colour.name} swatch.`

    const url = mint.routes.root === '/'
      ? `//${location.host}/products/${handle}`
      : `//${location.host}${mint.routes.root}/products/${handle}`

    const formattedProduct = {
      available: product.available,
      colour,
      current: isCurrentStyle,
      handle,
      styleId: stringToHandle(getProductTagValueByKey(tags, 'style_id')),
      url,
    }

    if (isCurrentStyle) {
      currentStyle = formattedProduct

      return false
    }

    return formattedProduct
  }).filter(Boolean)

  return [
    currentStyle,
    ...formattedStyles,
  ]
}

/**
 * Return an array of Collections that the product belongs to.
 * @param {Array} response - GraphQL Storefront response.collections
 * @returns {Array}
 */
function getFormattedCollections(collections) {
  return transformEdges(collections).map((collection) => {
    return collection.handle
  })
}

/**
 * Format and return a single 'Colour' object.
 * @param {Object} response - GraphQL Storefront response.
 * @returns {Object}
 */
function getFormattedColour(response) {
  const tags = response.tags

  return {
    group: stringToTitleCase(getProductTagValueByKey(tags, 'colour_group')),
    handle: stringToHandle(getProductTagValueByKey(tags, 'colour')),
    name: stringToTitleCase(getProductTagValueByKey(tags, 'colour')),
  }
}

/**
 * Format description from response.
 * - Each field will output an escaped HTML string.
 * - Later down the line, these will be cleaned up.
 * @returns {Object}
 */
function getFormattedDescriptions(product) {
  return {
    deliveryReturns: product.metaDeliveryReturns?.value || '',
    descriptionHtml: product.descriptionHtml,
    detailsCare: product.metaDetailsCare?.value || '',
    fabricComp: product.metaFabricComp?.value || '',
    sizeFit: product.metaSizeFit?.value || '',
    washCare: product.metaWashCare?.value || '',
    whyWeLoveIt: product.metaWhyWeLoveIt?.value || '',
    countryOfOrigin: product.metaCountryOfOrigin?.value || '',
    safetyInstructions: product.metaSafetyInstructions?.value || '',

    /**
     * Support for legacy Fusefabric metafields.
     */
    fusefabric: {
      detailsCare: product.metaDetailsCareFF?.value || '',
      sizeFit: product.metaSizeFitFF?.value || '',
      whyWeLoveIt: product.metaWhyWeLoveItFF?.value || '',
    },
  }
}

/**
 * Return images.
 * @param {Object} product - Unformatted GraphQL product.
 * @param {Object} currentFlexiFashionHandle - Formatted current FlexiFashion object.
 * @returns {Object}
 */
export function getFormattedImages(product, currentFlexiFashionHandle) {
  const useCloudinaryCDN = mint.flags.useCloudinaryCDN
  const productTitle = product.title
  let images = false

  if (useCloudinaryCDN && product.metaCloudinaryMediaInformation != null) {
    const imageAttribute = JSON.parse(product.metaCloudinaryMediaInformation.value)

    if (imageAttribute) {
      images = getFormattedCloudinaryImages(imageAttribute, productTitle)
    }
  }

  if (!useCloudinaryCDN || !images) {
    images = product.images?.edges
      ? transformEdges(product.images)
      : product.images

    images = images.map((image, index) => {
      const updatedSrc = updateProductImageCDN(image.url)

      return {
        aspectRatio: image.width / image.height,
        alt: image.altText || `${productTitle} (Image ${index})`,
        height: image.height,
        id: decodeApiId(image.id),
        src: updatedSrc,
        width: image.width,
      }
    })
  }

  /**
   * For FlexiFashion products, use media from the metaobject instead.
   * - If no FlexiFashion images exist, fallback to the default product images.
   * - If Cloudinary CDN is enabled, still use the default product images, as
   *   metaobject images do not currently come via Cloudinary.
   *
   * - To avoid reuploading the same packshots to every metaobject,
   *   fetch the index of the first packshot uploaded to the default product
   *   media and then remove every other image from the array.
   */
  if (currentFlexiFashionHandle && currentFlexiFashionHandle.media.length) {
    const packShot = getPackShotImage(images)
    const packshotIndex = packShot.index

    const packShots = images.slice(packshotIndex)

    images = [
      ...currentFlexiFashionHandle.media,
      ...packShots,
    ]
  }

  if (!images) {
    return false
  }

  return images
}

/**
 * Return formatted FlexiFashion details for the components.
 * @param {Object} product - Unformatted GraphQL product.
 * @param {Object} currentFlexiFashionHandle - Formatted current FlexiFashion object.
 * @returns {Object}
 */
function getFormattedFlexiFashion(currentFlexiFashionHandle) {
  if (!currentFlexiFashionHandle) {
    return false
  }

  return {
    handle: currentFlexiFashionHandle.handle,
    name: currentFlexiFashionHandle.name,
  }
}

/**
 * Return the formatted current FlexiFashion metaobject data.
 * - FlexiFashion will only be added if if both:
 *   1) Flexi parameter is appears in the URL
 *   2) The product has some FlexiFashion metafields with the same name.
 * - For the PDP, `mint-core.js` will check the URL parameter and pass
 *   that into store/product.js, where the product is queried.
 * @param {*} product - Unformatted GraphQL product.
 * @param {String} currentFlexiFashionHandle - Value from URL query parameter.
 * @returns {Boolean|Object}
 */
function getFormattedFlexiFashionData(product, currentFlexiFashionHandle) {

  /**
   * No handle passed in, or the product does not have FlexiFashion metafields.
   */
  if (!currentFlexiFashionHandle || !product.metaFlexiFashion?.references) {
    return false
  }

  const flexiMetaObjects = transformEdges(product.metaFlexiFashion.references)
  const flexiFashion = {}

  /**
   * Loop over each FlexiFashion metaobject and only format the currently
   * active FlexiFashion. We *could* format all of them, but currently the DOM
   * will only display one at a time. If in the future we want the customer to
   * be able to switch between these looks, then this can be modified to
   * return all of them.
   */
  for (const flexi of flexiMetaObjects) {
    const flexiFashionName = flexi.name.value.split('|')[1] || false

    if (!flexiFashionName) {
      continue
    }

    const formattedName = flexiFashionName.trim()
    const flexiHandle = stringToHandle(formattedName)

    if (flexiHandle !== currentFlexiFashionHandle) {
      continue
    }

    let metaobjectMedia = []

    /**
     * Loop over each media item image and format.
     */
    if (flexi.media?.references) {
      metaobjectMedia = transformEdges(flexi.media?.references).map((mediaReference, index) => {
        const item = mediaReference.image
        const updatedSrc = updateProductImageCDN(item.url)

        return {
          alt:  item.altText || `${product.title} with the '${formattedName}' look. (Image ${index})`,
          aspectRatio: item.width / item.height,
          height: item.height,
          id: decodeApiId(item.id),
          src: updatedSrc,
          width: item.width,
        }
      })
    }

    /**
     * Push to the object.
     */
    flexiFashion.handle = flexiHandle,
    flexiFashion.media = metaobjectMedia
    flexiFashion.name = formattedName
    flexiFashion.shopTheLook = flexi.shopTheLook?.value
  }

  return flexiFashion.name ? flexiFashion : false
}

/**
 * Return options and variable indicating if international sizes are enabled.
 * @param {Object} product - Unformatted GraphQL product.
 * @returns {Object}
 */
function getFormattedOptions(product) {
  let usesInternationalSizing = false

  const options = product.options.map((option, index) => {
    return {
      index,
      name: option.name,
      values: option.values,
    }
  })

  /**
   * International sizing check for each of the product's options.
   * - Only runs if an option's name contains `size`.
   */
  const internationalSizes = getProductIntlSizes(product)

  if (internationalSizes) {
    for (const option of options) {
      const isSizingOption = stringToHandle(option.name) === 'size'

      if (!isSizingOption) {
        continue
      }

      const intlValues = option.values.map(optionValue => {
        const match = getInternationalSizing(optionValue, internationalSizes)

        if (!match) {
          return false
        }

        return match
      }).filter(Boolean)

      if (intlValues.length === option.values.length) {
        option.values = intlValues
        usesInternationalSizing = true
      }
    }
  }

  return { options, usesInternationalSizing }
}

/**
 * Returns a formatted price object.
 * @param {String} price - GraphQL price response.
 * @returns {Object}
 */
function getFormattedPrice(price) {
  return {
    amount: (Number(price?.minVariantPrice.amount) * 100) ?? false,
    currencyCode: price?.minVariantPrice.currencyCode,
    max: (Number(price?.maxVariantPrice.amount) * 100) ?? false,
    min: (Number(price?.minVariantPrice.amount) * 100) ?? false,
    varies: (price?.maxVariantPrice.amount !== price?.minVariantPrice.amount),
  }
}

/**
 * Return an array for Shop the Look, if applicable.
 * - This relies on the raw GraphQL value - which is a stringified array
 *   of product IDs. We could update the GraphQL to get the handle, but
 *   what will it gain?
 *
 * - If there's active FlexiFashion products, then use those instead.
 * @param {Object} product - Unformatted GraphQL product.
 * @param {Object} currentFlexiFashion - Formatted current FlexiFashion object.
 */
function getFormattedShopTheLook({ product, currentFlexiFashion = false }) {
  const shopTheLookValue = currentFlexiFashion
    ? currentFlexiFashion.shopTheLook
    : product.metaShopTheLook?.value

  const shopTheLook = shopTheLookValue
    ? JSON.parse(shopTheLookValue)
    : false

  return shopTheLook
}

/**
 * Parse and return an array of variants.
 * @param {Object} product - Unformatted GraphQL product.
 * @param {Boolean} isGiftCard - If the product is a gift card.
 */
function getFormattedVariants(product, isGiftCard) {
  return transformEdges(product.variants).map((variant) => {
    const formattedVariant = {
      available: variant.available,
      compareAtPrice: {
        amount: (Number(variant.compareAtPrice?.amount) * 100) || false,
        currencyCode: variant.compareAtPrice?.currencyCode || false,
      },
      id: decodeApiId(variant.id),
      image: decodeApiId(variant.image?.id) || false,
      inventory: variant.quantityAvailable,
      price: {
        amount: (Number(variant.price.amount) * 100),
        currencyCode: variant.price.currencyCode,
      },
      selectedOptions: variant.selectedOptions.map((option) => {
        return {
          name: option.name,
          value: option.value,
        }
      }),
      sku: variant.sku,
    }

    let variantTitle = variant.title

    /**
     * International sizing check for each of the variant's selected options.
     * - Only runs if the option's name contains `size`.
     */
    const internationalSizes = getProductIntlSizes(product)

    if (internationalSizes) {
      for (const option of formattedVariant.selectedOptions) {
        const isSizingOption = stringToHandle(option.name) === 'size'

        if (!isSizingOption) {
          continue
        }

        const match = getInternationalSizing(option.value, internationalSizes)

        if (!match) {
          continue
        }

        variantTitle = variantTitle.replace(
          option.value,
          match,
        )

        option.value = match
      }
    }

    /**
     * For gift cards, add the value amount in the title.
     */
    formattedVariant.title = isGiftCard
      ? formatMoney(formattedVariant.price.amount, mint.shop.moneyFormat)
      : variantTitle

    return formattedVariant
  })
}

/**
 * Return a parsed JSON object of the product's international sizing metafield.
 * - If the metafield does not exist, return false.
 * - If the feature flag for localized sizes is disabled, return false.
 * @param {Object} product - Product object.
 * @returns {Boolean|Object}
 */
function getProductIntlSizes(product) {
  const featureFlag = mint.flags?.international?.showLocalisedSizes
  const sizes = product?.metaInternationalSize?.value

  if (!featureFlag || !sizes) {
    return false
  }

  return parseJSONUntilObject(sizes)
}
